import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Truck, Package, X, Command, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { cn } from '../../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Cmd+K listener
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery('');
      setResults([]);
    }
  }, [open]);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const headers = { Authorization: 'Bearer cookie-auth' };

      const [opsRes, contractsRes] = await Promise.allSettled([
        axios.get(`${API}/api/transport-requests?search=${encodeURIComponent(q)}&limit=5`, { headers }),
        axios.get(`${API}/api/contracts?search=${encodeURIComponent(q)}&limit=5`, { headers }),
      ]);

      const items = [];

      if (opsRes.status === 'fulfilled') {
        const ops = opsRes.value.data?.requests || opsRes.value.data || [];
        (Array.isArray(ops) ? ops : []).slice(0, 5).forEach((op) => {
          items.push({
            type: 'operation',
            icon: Truck,
            title: op.operationNumber || op.id,
            subtitle: `${op.departure || '?'} → ${op.arrival || '?'}`,
            path: `/operations/${op.id}`,
          });
        });
      }

      if (contractsRes.status === 'fulfilled') {
        const contracts = contractsRes.value.data?.contracts || contractsRes.value.data || [];
        (Array.isArray(contracts) ? contracts : []).slice(0, 5).forEach((c) => {
          items.push({
            type: 'contract',
            icon: FileText,
            title: c.contractNumber || c.id,
            subtitle: c.client || c.clientName || '',
            path: `/mes-contrats/${c.id}`,
          });
        });
      }

      setResults(items);
      setSelectedIdx(0);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query, search]);

  const handleSelect = (item) => {
    setOpen(false);
    navigate(item.path);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIdx]) {
      handleSelect(results[selectedIdx]);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="global-search-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setOpen(false)} />

      {/* Modal */}
      <div className="relative max-w-lg mx-auto mt-[15vh]" data-testid="global-search-modal">
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
            <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Rechercher opérations, contrats..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 outline-none text-sm text-gray-800 placeholder:text-gray-400"
              data-testid="global-search-input"
            />
            {query && (
              <button onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
            <kbd className="hidden sm:flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs font-mono">
              esc
            </kbd>
          </div>

          {/* Results */}
          <div className="max-h-72 overflow-y-auto">
            {loading && (
              <div className="px-4 py-8 text-center text-sm text-gray-400">Recherche...</div>
            )}
            {!loading && query.length >= 2 && results.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                Aucun résultat pour "{query}"
              </div>
            )}
            {!loading && results.map((item, i) => {
              const Icon = item.icon;
              return (
                <button
                  key={`${item.type}-${item.title}-${i}`}
                  onClick={() => handleSelect(item)}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                    i === selectedIdx ? 'bg-[#1db584]/10' : 'hover:bg-gray-50'
                  )}
                  data-testid={`search-result-${i}`}
                >
                  <div className={cn(
                    'p-1.5 rounded-lg',
                    item.type === 'operation' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'
                  )}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{item.title}</p>
                    <p className="text-xs text-gray-500 truncate">{item.subtitle}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                </button>
              );
            })}
          </div>

          {/* Footer hint */}
          {!query && (
            <div className="px-4 py-3 border-t border-gray-100 text-xs text-gray-400 flex items-center gap-4">
              <span className="flex items-center gap-1"><Package className="w-3 h-3" /> Tapez pour chercher</span>
              <span className="flex items-center gap-1"><kbd className="px-1 bg-gray-100 rounded text-[10px] font-mono">↑↓</kbd> naviguer</span>
              <span className="flex items-center gap-1"><kbd className="px-1 bg-gray-100 rounded text-[10px] font-mono">↵</kbd> ouvrir</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
